import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useProduct } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

export default function V2ProductDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useProduct(id ?? "");
  const product = (data as { product?: Row } | undefined)?.product;

  const name = str(product?.name) ?? "Product";
  const category = str(product?.category);
  const code = str(product?.product_code);
  const unitPrice = num(product?.unit_price);
  const unitType = str(product?.unit_type);
  const isActive = product?.is_active === true;

  const specs =
    product?.specifications &&
    typeof product.specifications === "object" &&
    !Array.isArray(product.specifications)
      ? (product.specifications as Record<string, unknown>)
      : null;
  const specEntries = specs ? Object.entries(specs) : [];

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !product}
      title={name}
      subtitle={category ?? undefined}
      breadcrumbs={[{ label: "Products", href: "/products" }, { label: name }]}
      notFoundMessage={{
        title: "Product not found",
        description: "This product may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Product" />}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          {/* Inline properties strip */}
          <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
            <StatusBadge status={isActive ? "active" : "inactive"} />
            {category && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span>{category}</span>
              </>
            )}
            {unitPrice != null && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span className="typo-mono font-medium">
                  {formatCurrency(unitPrice)}
                </span>
              </>
            )}
          </div>

          {/* KPI row */}
          <KPIRow
            cards={[
              {
                label: "Price",
                value: unitPrice != null ? formatCurrency(unitPrice) : "—",
                subtitle: unitType ? `per ${unitType}` : undefined,
              },
              { label: "Category", value: category ?? "—" },
              { label: "Unit", value: unitType ?? "—" },
              { label: "Status", value: isActive ? "Active" : "Inactive" },
            ]}
          />

          {/* Description */}
          {str(product?.description) && (
            <RailSection title="Description">
              <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                {product?.description as string}
              </p>
            </RailSection>
          )}

          {/* Specifications */}
          {specEntries.length > 0 && (
            <RailSection title="Specifications">
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-2">
                {specEntries.map(([key, value]) => (
                  <InfoRow
                    key={key}
                    label={key.replace(/_/g, " ")}
                    value={
                      value == null
                        ? null
                        : typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)
                    }
                  />
                ))}
              </div>
            </RailSection>
          )}
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Properties">
            <InfoRow label="Status" value={isActive ? "Active" : "Inactive"} />
            <InfoRow label="Category" value={category} />
            <InfoRow label="Code" value={code} />
            <InfoRow
              label="Unit Price"
              value={unitPrice != null ? formatCurrency(unitPrice) : null}
            />
            <InfoRow label="Unit Type" value={unitType} />
            <InfoRow
              label="Created"
              value={
                product?.created_at
                  ? formatDate(product.created_at as string)
                  : null
              }
            />
            <InfoRow
              label="Updated"
              value={
                product?.updated_at
                  ? formatDate(product.updated_at as string)
                  : null
              }
            />
          </RailSection>
        </div>
      </div>
    </DetailPage>
  );
}
